import { io } from 'socket.io-client';
import { SERVER_URL } from '../config';

const SOCKET_URL = SERVER_URL;

let socket = null;
let currentToken = null;
// Persistent listener registry — survives socket reconnects.
// Only cleared on full disconnect (logout/unmount).
let listeners = [];
// Room-join callbacks: things callers do once after a successful connect
// (e.g. `socket.emit('join:tenant', tenantId)`). The previous version
// joined once on initial connect, but transient disconnects + reconnects
// would leave the page silently un-subscribed. This registry is replayed
// on every fresh connect.
let joinHooks = [];

function applyListenersAndJoinHooks(sock) {
  for (const { event, callback } of listeners) {
    sock.on(event, callback);
  }
  for (const hook of joinHooks) {
    try { hook(sock); } catch (e) { console.warn('[Socket] joinHook error:', e?.message); }
  }
}

const socketService = {
  /**
   * Connect (or reconnect) with the given JWT token.
   * Token-aware and idempotent:
   *  - Same token + already connected → skip (no-op)
   *  - Different token or not connected → (re)create socket
   * Safe to call on every render / effect re-run (React StrictMode compatible).
   */
  connect(token) {
    // Already connected with the SAME token — nothing to do
    if (socket?.connected && token === currentToken) return socket;

    // Tear down old socket (stale token, failed handshake, StrictMode remount, etc.)
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }

    currentToken = token;

    console.log('[Socket] Creating connection to', SOCKET_URL);
    socket = io(SOCKET_URL, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 2000,
      // Infinite reconnects — was capped at 10 attempts, after which the
      // socket would die silently. The server allow-lists origins now and
      // the client side has nothing to lose by keeping retrying.
      reconnectionAttempts: Infinity,
      reconnectionDelayMax: 10000,
    });

    socket.on('connect', () => {
      console.log('[Socket] Connected, id:', socket.id);
      // Re-run room-join hooks on EVERY successful connect, including after
      // a transient disconnect / reconnect — not just the first time.
      for (const hook of joinHooks) {
        try { hook(socket); } catch (e) { console.warn('[Socket] joinHook error:', e?.message); }
      }
    });

    socket.on('connect_error', (err) => {
      console.warn('[Socket] Connection error:', err.message);
    });

    socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
    });

    // Re-apply ALL registered event listeners to the new socket
    applyListenersAndJoinHooks(socket);

    return socket;
  },

  /**
   * Swap the authentication token. Called after a successful JWT refresh so
   * the socket reconnects under the new access token (the previous version
   * kept using the stale token across the auth refresh boundary).
   */
  updateToken(newToken) {
    if (!newToken || newToken === currentToken) return;
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }
    this.connect(newToken);
  },

  /**
   * Full disconnect — only call on logout or app unmount.
   * Clears ALL listeners and join hooks.
   */
  disconnect() {
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }
    currentToken = null;
    listeners = [];
    joinHooks = [];
  },

  getSocket() {
    return socket;
  },

  /**
   * Register a listener. Persists across socket reconnects.
   * If socket exists, applies immediately. Otherwise applied on next connect().
   */
  on(event, callback) {
    listeners.push({ event, callback });
    if (socket) socket.on(event, callback);
  },

  /**
   * Remove a listener from both the registry and the live socket.
   */
  off(event, callback) {
    listeners = listeners.filter(
      (l) => !(l.event === event && l.callback === callback)
    );
    if (socket) socket.off(event, callback);
  },

  /**
   * Register a "join hook" — a callback that fires (a) immediately if the
   * socket is already connected and (b) on every successful (re)connect.
   * Pages with `socket.emit('join:room', id)` should use this so transient
   * network blips don't silently break their subscriptions.
   *
   * Returns an unregister function — call it on component unmount.
   */
  onConnect(callback) {
    joinHooks.push(callback);
    if (socket?.connected) {
      try { callback(socket); } catch (e) { console.warn('[Socket] joinHook error:', e?.message); }
    }
    return () => {
      joinHooks = joinHooks.filter((h) => h !== callback);
    };
  },
};

export default socketService;
